import {
  AppliedFilters,
  Badge,
  Button,
  EclipseBadge,
  ManageableItemsList,
} from '@payaca/ambrosia';
import { FC, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useRouteMatch } from 'react-router';

import useGetMaterialCategories from '@/api/queries/materials/useGetMaterialCategories';
import useGetMaterials from '@/api/queries/materials/useGetMaterials';
import useGetMyAvailableTags from '@/api/queries/me/useGetMyAvailableTags';
import { useGetAllPaginatedSuppliers } from '@/api/queries/suppliers/useGetSuppliers';
import { GetMaterialsInput, MaterialsQuery } from '@/gql/graphql';
import { getMaterialsFiltersLocalStorageKey } from '@/helpers/localStorageKeyHelper';
import { useTranslation } from '@/i18n';
import CreateMaterialModal from '@/ui/components/createEditMaterialModal/CreateMaterialModal';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { useHashFragment } from '@/utils/customHooks';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import Tag from '@payaca/components/tag/Tag';
import UntitledIcon from '@payaca/untitled-icons';
import { isNotNullish } from '@payaca/utilities/guards';
import ViewMaterialSidebar from './components/ViewMaterialSidebar';

type ListedMaterial = MaterialsQuery['materials']['items'][number];

const initialPagination = {
  limit: 100,
  offset: 0,
};

const ListedMaterialsPage: FC = () => {
  const translate = useTranslation();
  const history = useHistory();
  const [showCreateMaterialModal, toggleCreateMaterialModal] =
    useHashFragment('#create-material');

  const { path, url } = useRouteMatch();
  const isViewMaterialSidebarOpen = useRouteMatch<{
    materialId: string;
  }>(`${path}/:materialId`);

  const [getMaterialsPagination, setGetMaterialsPagination] = useState<{
    limit: number;
    offset: number;
  }>(initialPagination);

  const [getMaterialsInput, setGetMaterialsInput] = useState<GetMaterialsInput>(
    JSON.parse(
      localStorage.getItem(getMaterialsFiltersLocalStorageKey()) || '{}'
    )
  );

  const [searchTerm, setSearchTerm] = useState('');

  const { data: materials, isLoading } = useGetMaterials(
    getMaterialsPagination,
    getMaterialsInput
  );

  const { suppliers } = useGetAllPaginatedSuppliers({}, 100);
  const { materialCategories } = useGetMaterialCategories();
  const { data: availableTags } = useGetMyAvailableTags();

  const advancedFilterOptions = [
    {
      name: 'managedBy',
      title: 'Source',
      type: 'checkbox' as const,
      options: [
        { label: 'System', value: 'SYSTEM' },
        { label: 'My materials', value: 'SELF' },
      ],
    },
    {
      name: 'linkedToItems',
      title: 'Linked to items',
      type: 'radio' as const,
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
  ];

  if (materialCategories) {
    advancedFilterOptions.unshift({
      name: 'categories',
      title: 'Category',
      type: 'checkbox' as const,
      options: materialCategories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    });
  }

  if (suppliers) {
    advancedFilterOptions.unshift({
      name: 'suppliers',
      title: 'Supplier',
      type: 'checkbox' as const,
      options: suppliers.map((supplier) => ({
        label: supplier.name,
        value: supplier.id,
      })),
    });
  }

  if (availableTags) {
    advancedFilterOptions.unshift({
      name: 'tags',
      title: 'Tags',
      type: 'checkbox' as const,
      options: availableTags.map((tag) => ({
        label: tag.name,
        value: tag.id,
        colour: tag.colour,
      })),
    });
  }

  const appliedFilters = {
    managedBy: getMaterialsInput.managedBy || [],
    suppliers: getMaterialsInput.suppliers || [],
    categories: getMaterialsInput.categories || [],
    linkedToItems: isNotNullish(getMaterialsInput.linkedToItems)
      ? getMaterialsInput.linkedToItems
      : null,
    tags: getMaterialsInput.tags || [],
  };

  const handleApplyFilters = (filters: AppliedFilters) => {
    setGetMaterialsPagination(initialPagination);
    setGetMaterialsInput((s) => ({
      ...s,
      ...filters,
    }));

    localStorage.setItem(
      getMaterialsFiltersLocalStorageKey(),
      JSON.stringify(filters)
    );
  };

  return (
    <>
      <Helmet title={translate('pages.materials.title')} />
      <AuthenticatedPageWrapper>
        <div className="mb-16 p-4">
          <ManageableItemsList>
            <ManageableItemsList.HeaderBar
              heading="Materials"
              subHeading="What you order from suppliers, these are not shown to your customer."
              buttons={
                <>
                  <Button
                    size={'sm'}
                    onClick={() => toggleCreateMaterialModal()}
                  >
                    Create material
                  </Button>
                </>
              }
            />
            <ManageableItemsList.ActionBar>
              <ManageableItemsList.ActionBar.SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                onChangeTimeout={(value) =>
                  setGetMaterialsInput((s) => ({
                    ...s,
                    searchTerm: value,
                  }))
                }
                changeTimeoutMs={250}
              />
              <ManageableItemsList.ActionBar.AdvancedFilter
                onApplyFilters={handleApplyFilters}
                appliedFilters={appliedFilters}
                options={advancedFilterOptions}
              />
            </ManageableItemsList.ActionBar>
            <ManageableItemsList.Table
              isLoading={isLoading}
              items={materials?.materials.items || []}
              uniqueKey="id"
              onClickRow={(material) => {
                history.push(`/materials/${material.id}`);
              }}
            >
              <ManageableItemsList.Table.Column<ListedMaterial, 'image'>
                header="Image"
                field="image"
                render={(image, row) => {
                  if (!image?.thumbnailUrl) {
                    return (
                      <div className="flex h-14 w-14 items-center justify-center">
                        <UntitledIcon
                          name="image-01.3"
                          vectorEffect="non-scaling-stroke"
                          className="h-8 w-8 text-gray-700"
                        />
                      </div>
                    );
                  }

                  return (
                    <img
                      className="h-14 w-14 rounded-md"
                      alt={row.name}
                      src={image.thumbnailUrl}
                    />
                  );
                }}
                className="w-24"
              />
              <ManageableItemsList.Table.Column<ListedMaterial, 'name'>
                header="Material"
                field="name"
              />
              <ManageableItemsList.Table.Column<ListedMaterial, 'suppliedBy'>
                header="Supplier"
                field="suppliedBy"
                render={(suppliedBy) => {
                  if (!suppliedBy) {
                    return null;
                  }

                  return (
                    <EclipseBadge
                      badges={suppliedBy.map((i) => i.supplier.name)}
                      variant="soft"
                      colour="gray"
                    />
                  );
                }}
              />
              <ManageableItemsList.Table.Column<ListedMaterial, 'suppliedBy'>
                header="Price (ex tax)"
                field="suppliedBy"
                className="min-w-36 text-right"
                render={(suppliedBy) => {
                  if (!suppliedBy || suppliedBy.length === 0) {
                    return null;
                  }

                  return (
                    <CurrencyValue
                      value={suppliedBy[0].price.unitPriceExcTax.value}
                      currency={suppliedBy[0].price.unitPriceExcTax.currency}
                    />
                  );
                }}
              />
              <ManageableItemsList.Table.Column<ListedMaterial, 'category'>
                header="Category"
                field="category"
                render={(category) => {
                  if (!category) {
                    return null;
                  }

                  return (
                    <Badge variant="soft" colour="gray">
                      {category.name}
                    </Badge>
                  );
                }}
              />
              <ManageableItemsList.Table.Column<ListedMaterial, 'tags'>
                header="Tags"
                field="tags"
                render={(tags) => {
                  return (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <Tag key={tag.id} colour={tag.colour || 'gray'}>
                          {tag.name}
                        </Tag>
                      ))}
                    </div>
                  );
                }}
              />
            </ManageableItemsList.Table>

            <ManageableItemsList.PaginationBar
              type="custom"
              pageSize={getMaterialsPagination.limit}
              currentPage={
                getMaterialsPagination.offset / getMaterialsPagination.limit + 1
              }
              totalItems={materials?.materials.totalCount || 0}
              onPageChange={(newPage) => {
                setGetMaterialsPagination((s) => ({
                  ...s,
                  offset: (newPage - 1) * s.limit,
                }));
              }}
            />
          </ManageableItemsList>
        </div>

        <CreateMaterialModal
          isOpen={showCreateMaterialModal}
          onClose={toggleCreateMaterialModal}
          onPersistMaterialSuccess={(material) => {
            history.push(`/materials/${material.id}`);
          }}
        />
        <ViewMaterialSidebar
          isOpen={!!isViewMaterialSidebarOpen}
          materialId={isViewMaterialSidebarOpen?.params.materialId}
          onClose={() => history.push('/materials')}
        />
      </AuthenticatedPageWrapper>
    </>
  );
};

export default ListedMaterialsPage;
